import "./matchCaption.css";

/**
 * El rotulo de retransmision del partido: una franja abajo, en el centro, que
 * entra, se lee y se va. Lo usan los cambios de plan de la IA
 * (`game/commentary.ts`) y los consejos de la primera vez (el cuerpo con la O).
 *
 * Abajo en el centro, JUSTO ENCIMA del marcador (que tambien vive ahi):
 * arriba estan el minimapa y el objetivo de la carrera, y en tactil las
 * esquinas de abajo son del joystick y los botones. Como un rotulo de
 * television sobre la chapa del resultado. No atrapa clics: es para leer.
 */
export type CaptionTone = "chase" | "protect" | "style" | "tip";

export interface CaptionContent {
  /** Etiqueta corta de arriba: el equipo, o "CONSEJO". */
  kicker: string;
  title: string;
  sub: string;
  tone: CaptionTone;
}

/** Segundos en pantalla. Da para leer dos lineas cortas sin soltar el mando. */
const SHOW_MS = 4600;
/** Cuantos pueden esperar turno: si se juntan mas, los viejos ya no son noticia. */
const QUEUE_MAX = 2;

export function createMatchCaption(parent: HTMLElement) {
  const root = document.createElement("div");
  root.className = "match-caption";
  root.setAttribute("aria-live", "polite");
  root.innerHTML = `<span class="match-caption-kicker"></span><strong class="match-caption-title"></strong><span class="match-caption-sub"></span>`;
  parent.appendChild(root);
  const kicker = root.querySelector<HTMLElement>(".match-caption-kicker")!;
  const title = root.querySelector<HTMLElement>(".match-caption-title")!;
  const sub = root.querySelector<HTMLElement>(".match-caption-sub")!;

  const queue: CaptionContent[] = [];
  let timer = 0;
  let showing = false;

  const next = (): void => {
    const item = queue.shift();
    if (!item) {
      showing = false;
      root.classList.remove("is-shown");
      return;
    }
    showing = true;
    kicker.textContent = item.kicker;
    title.textContent = item.title;
    sub.textContent = item.sub;
    root.dataset.tone = item.tone;
    root.classList.add("is-shown");
    timer = window.setTimeout(() => {
      root.classList.remove("is-shown");
      // Lo que tarda en irse (CSS) antes del siguiente.
      timer = window.setTimeout(next, 320);
    }, SHOW_MS);
  };

  return {
    show(content: CaptionContent): void {
      if (queue.length >= QUEUE_MAX) queue.shift();
      queue.push(content);
      if (!showing) next();
    },
    /**
     * Distancia al borde de abajo, en px: justo encima del marcador, que
     * tambien vive abajo en el centro (`scoreBugClearance`).
     */
    setBottom(px: number): void {
      root.style.setProperty("--caption-bottom", `${Math.round(px)}px`);
    },
    /** Fuera todo: se acaba el partido o se vuelve al menu. */
    clear(): void {
      clearTimeout(timer);
      queue.length = 0;
      showing = false;
      root.classList.remove("is-shown");
    },
    dispose(): void {
      clearTimeout(timer);
      root.remove();
    },
  };
}

export type MatchCaption = ReturnType<typeof createMatchCaption>;
